import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiBell,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiCommand,
  FiExternalLink,
  FiLink,
  FiRefreshCw,
  FiSave,
  FiSend,
  FiShield,
} from 'react-icons/fi';
import api from '../api';
import { mediaUrl } from '../utils/media';

const emptyNotificationForm = {
  title: '',
  body: '',
  level: 'info',
  actionLabel: '',
  actionUrl: '',
};

function profileToForm(profile = {}) {
  const address = profile.address || {};
  return {
    legalName: profile.legalName || '',
    displayName: profile.displayName || '',
    businessType: profile.businessType || '',
    registrationNumber: profile.registrationNumber || '',
    taxId: profile.taxId || '',
    website: profile.website || '',
    phone: profile.phone || '',
    email: profile.email || '',
    description: profile.description || '',
    addressLine1: address.line1 || '',
    addressLine2: address.line2 || '',
    city: address.city || '',
    region: address.region || '',
    district: address.district || '',
    country: address.country || '',
    postalCode: address.postalCode || '',
  };
}

function formToProfile(form) {
  return {
    legalName: form.legalName,
    displayName: form.displayName,
    businessType: form.businessType,
    registrationNumber: form.registrationNumber,
    taxId: form.taxId,
    website: form.website,
    phone: form.phone,
    email: form.email,
    description: form.description,
    address: {
      line1: form.addressLine1,
      line2: form.addressLine2,
      city: form.city,
      region: form.region,
      district: form.district,
      country: form.country,
      postalCode: form.postalCode,
    },
  };
}

export default function BusinessPortfolioManager({ userId }) {
  const [detail, setDetail] = useState(null);
  const [commandSheet, setCommandSheet] = useState(null);
  const [form, setForm] = useState(() => profileToForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [commandBusy, setCommandBusy] = useState('');
  const [error, setError] = useState('');
  const [commandError, setCommandError] = useState('');
  const [message, setMessage] = useState('');
  const [notificationForm, setNotificationForm] = useState(emptyNotificationForm);

  const loadDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setCommandError('');
    try {
      const [profileResult, commandResult] = await Promise.allSettled([
        api.get(`/admin/platform/business-profiles/${userId}`),
        api.get(`/admin/platform/business-profiles/${userId}/command-sheet`),
      ]);

      if (profileResult.status !== 'fulfilled') {
        throw profileResult.reason;
      }

      const profileData = profileResult.value.data;
      setDetail(profileData);
      setForm(profileToForm(profileData.businessProfile || {}));

      if (commandResult.status === 'fulfilled') {
        setCommandSheet(commandResult.value.data || null);
      } else {
        setCommandSheet(null);
        setCommandError(
          commandResult.reason?.response?.data?.message || 'Could not load the cross-product command sheet',
        );
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load this business profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const record = detail?.record || null;
  const avatarUrl = useMemo(() => mediaUrl(record?.avatarUrl || ''), [record?.avatarUrl]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleNotificationChange = (event) => {
    const { name, value } = event.target;
    setNotificationForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.patch(`/admin/platform/business-profiles/${userId}`, {
        businessProfile: formToProfile(form),
      });
      setMessage(data.message || 'Business profile updated.');
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update this business profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNotification = async (event) => {
    event.preventDefault();
    if (!record) return;
    setSending(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.post('/admin/platform/notifications', {
        ...notificationForm,
        recipient: record.userId,
      });
      setMessage(data.message || 'Support notification sent.');
      setNotificationForm(emptyNotificationForm);
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send a support notification');
    } finally {
      setSending(false);
    }
  };

  const handleCommandAction = async (action) => {
    if (!action) return;
    if (action.href) {
      window.open(action.href, '_blank', 'noopener,noreferrer');
      return;
    }

    const requiresConfirm = action.requiresConfirm === true;
    if (
      requiresConfirm &&
      !window.confirm(`Run "${action.label}" on ${action.productLabel || action.productKey || 'this product'}?`)
    ) {
      return;
    }

    setCommandBusy(action.key || action.action);
    setCommandError('');
    setMessage('');
    try {
      const { data } = await api.post('/admin/platform/command-actions', {
        productKey: action.productKey,
        action: action.action,
        payload: action.payload || {},
        confirm: requiresConfirm,
      });
      setMessage(data.message || 'Command action completed.');
      await loadDetail();
    } catch (err) {
      const response = err?.response?.data;
      if (response?.requiresConfirm) {
        const confirmed = window.confirm(response.message || 'This action needs confirmation.');
        if (confirmed) {
          try {
            const { data } = await api.post('/admin/platform/command-actions', {
              productKey: action.productKey,
              action: action.action,
              payload: action.payload || {},
              confirm: true,
            });
            setMessage(data.message || 'Command action completed.');
            await loadDetail();
          } catch (retryErr) {
            setCommandError(
              retryErr?.response?.data?.message || 'Could not complete this command action',
            );
          }
        }
      } else {
        setCommandError(response?.message || 'Could not complete this command action');
      }
    } finally {
      setCommandBusy('');
    }
  };

  if (loading) {
    return (
      <div className="rounded-[2rem] border border-gray-800 bg-gray-950 p-8 text-sm text-slate-300 shadow-xl">
        Loading suite profile...
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="rounded-[2rem] border border-red-500/30 bg-red-950/20 p-8 text-sm text-red-200">
        {error}
      </div>
    );
  }

  if (!record) {
    return (
      <div className="rounded-[2rem] border border-gray-800 bg-gray-950 p-8 text-sm text-slate-300 shadow-xl">
        Select a customer profile to inspect the suite business state.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top,rgba(245,178,33,0.2),transparent_32%),linear-gradient(180deg,#111111,#030303)] p-8 shadow-2xl">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-center gap-4">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={record.displayName}
                className="h-20 w-20 rounded-3xl object-cover ring-2 ring-amber-300/40"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-amber-400/15 text-3xl text-amber-200 ring-2 ring-amber-300/30">
                <FiBriefcase />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-amber-200">Suite Business View</p>
              <h2 className="mt-2 text-3xl font-black tracking-[-0.03em] text-white">
                {record.businessName || record.displayName}
              </h2>
              <p className="mt-2 text-sm text-slate-300">
                {record.displayName} · {record.email || 'No email yet'} · {record.shimbirrId || 'No Shimbirr ID'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <StatusPill status={record.businessStatus} />
            <button
              type="button"
              onClick={loadDetail}
              className="inline-flex items-center gap-2 rounded-full border border-[#f5b221]/20 px-4 py-2 text-sm font-semibold text-[#f7d88a] hover:bg-[#f5b221]/10"
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Readiness" value={`${record.readinessScore || 0}%`} accent="text-yellow-300" />
          <MetricCard label="Connected portals" value={String(record.totalConnectedPortals || 0)} accent="text-white" />
          <MetricCard label="Service items" value={String(record.totalServiceItems || 0)} accent="text-amber-200" />
          <MetricCard label="Open issues" value={String(record.issueCount || 0)} accent={record.issueCount ? 'text-[#ff6d00]' : 'text-[#f5b221]'} />
        </div>
      </section>

      {message ? (
        <div className="rounded-2xl border border-[#f5b221]/30 bg-[#f5b221]/10 px-4 py-3 text-sm text-[#fff0c3]">
          {message}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-[#ff6d00]/30 bg-[#ff6d00]/10 px-4 py-3 text-sm text-[#ffd9c0]">
          {error}
        </div>
      ) : null}
      {commandError ? (
        <div className="rounded-2xl border border-[#ff6d00]/30 bg-[#ff6d00]/10 px-4 py-3 text-sm text-[#ffd9c0]">
          {commandError}
        </div>
      ) : null}

      <section className="rounded-[2rem] border border-[#4e4632] bg-[#090909] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <FiCommand className="text-amber-300" />
              <h3 className="text-2xl font-bold text-white">Cross-product command sheet</h3>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              This layer merges Shimbirr with PMS, Taban Food, and Taban Website so admins can
              inspect one business across the whole stack and run safe product actions from one place.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {(commandSheet?.sources || []).map((source) => (
              <div
                key={source.productKey}
                className="rounded-full border border-[#4e4632] bg-[#111111] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]"
              >
                {source.productLabel} · {source.stale ? 'stale' : source.status || 'pending'}
              </div>
            ))}
            <button
              type="button"
              onClick={loadDetail}
              className="inline-flex items-center gap-2 rounded-full border border-[#f5b221]/20 px-4 py-2 text-sm font-semibold text-[#f7d88a] hover:bg-[#f5b221]/10"
            >
              <FiRefreshCw /> Refresh sheet
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-5 xl:grid-cols-[1.2fr,0.8fr]">
          <div className="space-y-4">
            {commandSheet?.remoteProducts?.length ? (
              commandSheet.remoteProducts.map((product) => (
                <div key={product.productKey} className="rounded-[1.5rem] border border-white/5 bg-black/40 p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h4 className="text-lg font-bold text-white">{product.productLabel}</h4>
                        <SeverityPill severity={product.ok ? 'healthy' : 'critical'} label={product.ok ? 'live' : 'unavailable'} />
                      </div>
                      {!product.ok ? (
                        <p className="mt-3 text-sm text-[#ffd9c0]">{product.error}</p>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {product.links?.adminUrl ? (
                        <a
                          href={product.links.adminUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white/5"
                        >
                          <FiExternalLink /> Open source
                        </a>
                      ) : null}
                      {(product.actions || []).map((action) => (
                        <button
                          key={action.key}
                          type="button"
                          onClick={() => handleCommandAction(action)}
                          disabled={commandBusy === action.key}
                          className="rounded-full border border-[#f5b221]/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#fff0c3] transition hover:bg-[#f5b221]/10 disabled:opacity-60"
                        >
                          {commandBusy === action.key ? 'Working...' : action.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {product.summaryCards?.length ? (
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                      {product.summaryCards.map((card) => (
                        <MetricCard
                          key={`${product.productKey}:${card.key}`}
                          label={card.label}
                          value={String(card.value ?? 0)}
                          accent="text-white"
                        />
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    <InfoBox
                      label="Queues"
                      value={
                        product.queues?.length
                          ? product.queues.map((item) => `${item.label}: ${item.value}`).join(' • ')
                          : 'No queue data'
                      }
                    />
                    <InfoBox
                      label="Renewals"
                      value={
                        product.renewals?.length
                          ? product.renewals
                              .slice(0, 2)
                              .map((item) => `${item.label} (${item.daysLeft}d)`)
                              .join(' • ')
                          : 'No renewal pressure'
                      }
                    />
                    <InfoBox
                      label="References"
                      value={
                        product.references?.length
                          ? product.references
                              .slice(0, 2)
                              .map((item) => `${item.label} · ${item.meta}`)
                              .join(' • ')
                          : 'No linked references'
                      }
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
                No connected product drilldowns have been linked to this business yet.
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-[1.5rem] border border-white/5 bg-black/40 p-5">
              <div className="flex items-center gap-2">
                <FiActivity className="text-amber-300" />
                <h4 className="text-lg font-bold text-white">Unified timeline</h4>
              </div>
              <div className="mt-4 space-y-3">
                {commandSheet?.timeline?.length ? (
                  commandSheet.timeline.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-white/5 bg-black/30 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-white">{item.label}</p>
                          <p className="mt-2 text-sm leading-6 text-slate-300">{item.description}</p>
                        </div>
                        <SeverityPill severity={item.status} label={item.source || item.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
                        <span>{formatDateTime(item.timestamp)}</span>
                        {item.actionUrl ? (
                          <a
                            href={item.actionUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[#f7d88a]"
                          >
                            <FiExternalLink /> Open
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
                    No cross-product activity has been recorded yet.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-white/5 bg-black/40 p-5">
              <div className="flex items-center gap-2">
                <FiClock className="text-amber-300" />
                <h4 className="text-lg font-bold text-white">Sheet freshness</h4>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <InfoBox
                  label="Stale sources"
                  value={String(commandSheet?.freshness?.staleSourceCount || 0)}
                />
                <InfoBox
                  label="Live sources"
                  value={String(commandSheet?.freshness?.liveSourceCount || 0)}
                />
                <InfoBox
                  label="Snapshot sources"
                  value={String(commandSheet?.freshness?.snapshotSourceCount || 0)}
                />
                <InfoBox
                  label="Local actions"
                  value={String(commandSheet?.actions?.length || 0)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 2xl:grid-cols-[1.25fr,0.95fr]">
        <form
          onSubmit={handleSave}
          className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-white">Business profile</h3>
              <p className="mt-2 text-sm text-slate-400">
                Admin edits here follow the live Taban suite business profile model so support can fix real customer setup problems without creating parallel records.
              </p>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:opacity-90 disabled:opacity-60"
            >
              <FiSave /> {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <InputField label="Legal name" name="legalName" value={form.legalName} onChange={handleFormChange} />
            <InputField label="Display name" name="displayName" value={form.displayName} onChange={handleFormChange} />
            <InputField label="Business type" name="businessType" value={form.businessType} onChange={handleFormChange} />
            <InputField label="Registration number" name="registrationNumber" value={form.registrationNumber} onChange={handleFormChange} />
            <InputField label="Tax ID" name="taxId" value={form.taxId} onChange={handleFormChange} />
            <InputField label="Website" name="website" value={form.website} onChange={handleFormChange} />
            <InputField label="Business email" name="email" type="email" value={form.email} onChange={handleFormChange} />
            <InputField label="Business phone" name="phone" value={form.phone} onChange={handleFormChange} />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <InputField label="Address line 1" name="addressLine1" value={form.addressLine1} onChange={handleFormChange} />
            <InputField label="Address line 2" name="addressLine2" value={form.addressLine2} onChange={handleFormChange} />
            <InputField label="City" name="city" value={form.city} onChange={handleFormChange} />
            <InputField label="Region" name="region" value={form.region} onChange={handleFormChange} />
            <InputField label="District" name="district" value={form.district} onChange={handleFormChange} />
            <InputField label="Country" name="country" value={form.country} onChange={handleFormChange} />
            <InputField label="Postal code" name="postalCode" value={form.postalCode} onChange={handleFormChange} />
          </div>

          <div className="mt-4">
            <TextAreaField
              label="Support notes"
              name="description"
              value={form.description}
              onChange={handleFormChange}
            />
          </div>
        </form>

        <div className="space-y-6">
          <section className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl">
            <div className="flex items-center gap-2">
              <FiShield className="text-amber-200" />
              <h3 className="text-xl font-semibold text-white">Suite attention map</h3>
            </div>
            <div className="mt-4 space-y-3">
              {record.issues?.length ? (
                record.issues.map((issue) => (
                  <div
                    key={issue.key}
                    className="rounded-2xl border border-white/5 bg-black/40 p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-white">{issue.title}</p>
                      <SeverityPill severity={issue.severity} />
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{issue.detail}</p>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-[#f5b221]/20 bg-[#f5b221]/10 p-4 text-sm text-[#fff0c3]">
                  This account is currently clean. No suite business issues are blocking support actions.
                </div>
              )}
            </div>
          </section>

          <section className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl">
            <div className="flex items-center gap-2">
              <FiBell className="text-amber-300" />
              <h3 className="text-xl font-semibold text-white">Direct support ping</h3>
            </div>
            <form onSubmit={handleSendNotification} className="mt-4 space-y-4">
              <InputField label="Title" name="title" value={notificationForm.title} onChange={handleNotificationChange} required />
              <TextAreaField label="Body" name="body" value={notificationForm.body} onChange={handleNotificationChange} required rows={4} />
              <div className="grid gap-4 md:grid-cols-3">
                <SelectField label="Level" name="level" value={notificationForm.level} onChange={handleNotificationChange}>
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </SelectField>
                <InputField label="Action label" name="actionLabel" value={notificationForm.actionLabel} onChange={handleNotificationChange} />
                <InputField label="Action URL" name="actionUrl" value={notificationForm.actionUrl} onChange={handleNotificationChange} />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-3 text-sm font-semibold text-black transition hover:opacity-90 disabled:opacity-60"
              >
                <FiSend /> {sending ? 'Sending...' : 'Send to this user'}
              </button>
            </form>
          </section>
        </div>
      </section>

      <section className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl">
        <div className="flex items-center gap-2">
          <FiLink className="text-amber-300" />
          <h3 className="text-xl font-semibold text-white">Connected portals</h3>
        </div>
        <div className="mt-5 space-y-4">
          {record.connectedPortals?.length ? (
            record.connectedPortals.map((portal) => (
              <div key={portal.clientId} className="rounded-3xl border border-white/5 bg-black/40 p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h4 className="text-lg font-semibold text-white">{portal.label}</h4>
                      <SeverityPill severity={portal.attentionLevel} label={portal.attentionLevel} />
                      <span className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-slate-300">
                        {portal.connectionMode || 'oauth'}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-300">
                      {portal.origin || 'No origin saved'} · {portal.serviceCount || 0} service item{portal.serviceCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <PortalActions actions={portal.actions} />
                </div>

                <div className="mt-4 grid gap-3 lg:grid-cols-3">
                  <InfoBox label="Allowed scopes" value={portal.allowedScopes?.length ? portal.allowedScopes.join(', ') : 'No scope grant saved'} />
                  <InfoBox label="Categories" value={portal.categories?.length ? portal.categories.join(', ') : 'No categories yet'} />
                  <InfoBox label="Last paired" value={formatDateTime(portal.lastPairedAt)} />
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
              No portal has synchronized suite data to this Shimbirr profile yet.
            </div>
          )}
        </div>
      </section>

      <section className="rounded-[2rem] border border-gray-800 bg-gray-950 p-6 shadow-xl">
        <div className="flex items-center gap-2">
          <FiBell className="text-amber-200" />
          <h3 className="text-xl font-semibold text-white">Recent customer notifications</h3>
        </div>
        <div className="mt-5 space-y-3">
          {detail?.recentNotifications?.length ? (
            detail.recentNotifications.map((notification) => (
              <div key={notification._id} className="rounded-2xl border border-white/5 bg-black/40 p-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-semibold text-white">{notification.title}</p>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    {formatDateTime(notification.createdAt)}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-300">{notification.body}</p>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-slate-400">
              No support or suite notification history is saved for this user yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, accent = 'text-white' }) {
  return (
    <div className="rounded-3xl border border-white/5 bg-black/30 p-5">
      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{label}</p>
      <p className={`mt-2 text-3xl font-black tracking-[-0.03em] ${accent}`}>{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const normalized = String(status || '').toLowerCase();
  const classes = {
    suite_ready: 'border-[#f5b221]/30 bg-[#f5b221]/12 text-[#fff0c3]',
    needs_attention: 'border-[#ff6d00]/30 bg-[#ff6d00]/10 text-[#ffd9c0]',
    attention: 'border-[#ff6d00]/30 bg-[#ff6d00]/10 text-[#ffd9c0]',
    personal_only: 'border-[#6d6554]/30 bg-[#2a2a2a] text-[#d2c5ab]',
  }[normalized] || 'border-[#6d6554]/30 bg-[#2a2a2a] text-[#d2c5ab]';

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] ${classes}`}>
      {normalized === 'suite_ready' ? <FiCheckCircle /> : <FiAlertCircle />}
      {normalized.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
}

function SeverityPill({ severity, label }) {
  const normalized = String(severity || '').toLowerCase();
  const classes = {
    critical: 'border-[#ff6d00]/30 bg-[#ff6d00]/10 text-[#ffd9c0]',
    warning: 'border-[#ff6d00]/30 bg-[#ff6d00]/10 text-[#ffd9c0]',
    healthy: 'border-[#f5b221]/30 bg-[#f5b221]/10 text-[#fff0c3]',
    info: 'border-[#6d6554]/30 bg-[#2a2a2a] text-[#d2c5ab]',
  }[normalized] || 'border-[#6d6554]/30 bg-[#2a2a2a] text-[#d2c5ab]';

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] ${classes}`}>
      {(label || normalized || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

function InputField({ label, name, value, onChange, type = 'text', required = false }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.22em] text-slate-400">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-white outline-none transition focus:border-amber-300"
      />
    </label>
  );
}

function TextAreaField({ label, name, value, onChange, required = false, rows = 5 }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.22em] text-slate-400">{label}</span>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={rows}
        className="w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-white outline-none transition focus:border-amber-300"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.22em] text-slate-400">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-white outline-none transition focus:border-amber-300"
      >
        {children}
      </select>
    </label>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-black/30 p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm leading-6 text-white">{value || '—'}</p>
    </div>
  );
}

function PortalActions({ actions = {} }) {
  const entries = Object.entries(actions).filter(([, value]) => value);
  if (!entries.length) {
    return (
      <div className="rounded-full border border-white/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-400">
        No saved portal actions
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([key, value]) => (
        <a
          key={key}
          href={value}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white/5"
        >
          <FiExternalLink /> {key.replace(/([A-Z])/g, ' $1').trim()}
        </a>
      ))}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return 'Not recorded';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}
